// Generic sortable-table hook. Each call provides:
//   - rows: the source data
//   - accessors: a record of `{ columnKey: row => sortableValue }`
//   - initial: optional default sort column + direction
//
// Returns sorted rows + the current sort state + a toggle handler
// the column header buttons can wire up.
//
// Null/undefined values always sink to the bottom regardless of
// direction — sorting by a column should always pull the populated
// rows to where the operator's attention lives. Mixing nulls into
// the comparison whipsaws the order on every direction toggle.
//
// Strings compare via localeCompare (case-insensitive, locale-aware).
// Numbers compare via subtraction. Mixed types fall back to string
// comparison — fine for our tables, none mix typed columns yet.

import { useMemo, useState } from "react";

export type SortDir = "asc" | "desc";
export type SortAccessor<T> = (row: T) => number | string | boolean | null | undefined;

export interface SortableTableState {
  /** Currently-sorted column key, or null when no column is active. */
  sortKey: string | null;
  /** Sort direction for the active column. */
  sortDir: SortDir;
  /** Toggle: clicking the same column flips direction; new column resets to "desc". */
  toggleSort: (key: string) => void;
}

export function useSortableTable<T>(
  rows: T[],
  accessors: Record<string, SortAccessor<T>>,
  initial?: { key: string; dir?: SortDir },
): { sortedRows: T[] } & SortableTableState {
  const [sortKey, setSortKey] = useState<string | null>(initial?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initial?.dir ?? "desc");

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const get = accessors[sortKey];
    if (!get) return rows;
    const sign = sortDir === "asc" ? 1 : -1;
    const cmp = (a: T, b: T): number => {
      const av = get(a);
      const bv = get(b);
      // Nulls always last regardless of dir.
      const aNull = av == null || av === "";
      const bNull = bv == null || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
      if (typeof av === "boolean" && typeof bv === "boolean") {
        return ((av ? 1 : 0) - (bv ? 1 : 0)) * sign;
      }
      return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * sign;
    };
    return [...rows].sort(cmp);
  }, [rows, sortKey, sortDir, accessors]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Default to desc on new column — most useful for "biggest first"
      // (volume, position numerically descending = highest rank, count, etc.)
      setSortDir("desc");
    }
  };

  return { sortedRows, sortKey, sortDir, toggleSort };
}
